import type { UserInfo } from "@freightsimple/generated-dashboard-openapi-client/models/UserInfo";
import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import type { UUID } from "@/Shared/types";

/**
 * Reading a signed-in user back out of localStorage from before the user shape
 * gained phone, company and linkedCompanies.
 *
 * This exists only for people whose browser still holds the old shape — it is
 * pure data reshaping with no bearing on how auth works, which is why it lives
 * here rather than in the slice.
 */

type UserV0 = {
  userId: string;
  email: string;
  name: string;
  companyName: string;
  companyId: UUID;
  allowedToSwitchToLinkedCompanies?: boolean | undefined;
};

// Including companyName and companyId twice just to avoid changing more code for now
export type UserV1 = UserInfo & {
  companyName: string;
  companyId: string;
};

/**
 * Type guard to check if a user object is UserV0 (old format)
 * UserV0 is missing fields like phone, company, linkedCompanies, etc.
 */
export function isUserV0(user: unknown): user is UserV0 {
  return (
    user instanceof Object &&
    "userId" in user &&
    "email" in user &&
    "companyId" in user &&
    // UserV1 has these fields but UserV0 doesn't
    !("phone" in user) &&
    !("company" in user) &&
    !("linkedCompanies" in user)
  );
}

/**
 * Migrates UserV0 to UserV1 format with placeholder data
 * The placeholder data will be replaced with real data from server on next refresh
 */
export function migrateUserV0ToV1(userV0: UserV0): UserV1 {
  return {
    ...userV0,
    phone: "", // Will be refreshed from server
    phoneExtension: "",
    company: {
      companyId: userV0.companyId,
      companyName: userV0.companyName,
      isCurrentCompany: true,
      currency: Currency.Cad, // Default currency for migrated users
    },
    linkedCompanies: [],
    isLinkedCompany: false,
  };
}
